import { useCallback } from "react";

import { showToast } from "@/features/shared/components";
import {
  ParsedWidget,
  parseWidgetClipboardText,
} from "@/features/admin/features/widgets/widget-clipboard";
import { useTranslation } from "@/lib/i18n";

/**
 * Reads a widget from the system clipboard. Returns the parsed widget (values
 * plus optional source layout), or `null` (with an error toast) when the
 * clipboard is empty, unreadable, or does not contain a valid widget payload.
 * Must be called from a user gesture so the browser grants clipboard read
 * access.
 */
export function usePasteWidget() {
  const { t } = useTranslation();

  return useCallback(async (): Promise<ParsedWidget | null> => {
    let text: string;
    try {
      text = await navigator.clipboard.readText();
    } catch {
      showToast(t("admin.messages.widget_paste_invalid"), { type: "error" });
      return null;
    }

    const parsed = parseWidgetClipboardText(text);
    if (!parsed) {
      showToast(t("admin.messages.widget_paste_invalid"), { type: "error" });
      return null;
    }

    return parsed;
  }, [t]);
}
